import { ChainFamily, getChainMetadata, hasEvmIdentifiers, resolveChain, registry } from './chunk-LKQ6GCAX.js';
import { createLogger } from './chunk-GPJ572YS.js';
import { brandClass, globalSingleton } from './chunk-N7SZ66QO.js';
import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/api/cache.ts
var MemoryRequestCache = class {
  static {
    brandClass(this, "api/MemoryRequestCache");
  }
  store = /* @__PURE__ */ new Map();
  maxEntries;
  defaultTtlMs;
  constructor(opts = {}) {
    this.maxEntries = opts.maxEntries ?? 200;
    this.defaultTtlMs = opts.defaultTtlMs ?? 3e4;
  }
  get(key) {
    const entry = this.store.get(key);
    if (!entry) return void 0;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return void 0;
    }
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }
  set(key, value, ttlMs = this.defaultTtlMs) {
    if (this.store.size >= this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest !== void 0) this.store.delete(oldest);
    }
    this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }
  delete(key) {
    this.store.delete(key);
  }
  clear() {
    this.store.clear();
  }
  keys() {
    return [...this.store.keys()];
  }
};
var LocalStorageRequestCache = class {
  static {
    brandClass(this, "api/LocalStorageRequestCache");
  }
  prefix;
  getStorage;
  constructor(opts = {}) {
    this.prefix = `${opts.namespace ?? "authe:api-cache"}:`;
    this.getStorage = opts.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
  }
  keyFor(key) {
    return `${this.prefix}${key}`;
  }
  get(key) {
    const storage = this.getStorage();
    if (!storage) return void 0;
    try {
      const raw = storage.getItem(this.keyFor(key));
      if (!raw) return void 0;
      const entry = JSON.parse(raw);
      if (Date.now() > entry.expiresAt) {
        storage.removeItem(this.keyFor(key));
        return void 0;
      }
      return entry.value;
    } catch {
      return void 0;
    }
  }
  set(key, value, ttlMs) {
    const storage = this.getStorage();
    if (!storage) return;
    try {
      const entry = { value, expiresAt: Date.now() + ttlMs };
      storage.setItem(this.keyFor(key), JSON.stringify(entry));
    } catch {
    }
  }
  delete(key) {
    const storage = this.getStorage();
    if (!storage) return;
    try {
      storage.removeItem(this.keyFor(key));
    } catch {
    }
  }
  clear() {
    const storage = this.getStorage();
    if (!storage) return;
    try {
      const toRemove = [];
      for (let i = 0; i < storage.length; i += 1) {
        const k = storage.key(i);
        if (k && k.startsWith(this.prefix)) toRemove.push(k);
      }
      for (const k of toRemove) storage.removeItem(k);
    } catch {
    }
  }
  keys() {
    const storage = this.getStorage();
    if (!storage) return [];
    try {
      const out = [];
      for (let i = 0; i < storage.length; i += 1) {
        const k = storage.key(i);
        if (k && k.startsWith(this.prefix)) out.push(k.slice(this.prefix.length));
      }
      return out;
    } catch {
      return [];
    }
  }
};
var NO_CACHE = {
  get: () => void 0,
  set: () => void 0,
  delete: () => void 0,
  clear: () => void 0,
  keys: () => []
};
function writeInvalidationFragment(path) {
  const first = path.split("?")[0].split("/").find((segment) => segment.length > 0);
  return first ? `/${first}` : null;
}
function cacheKey(method, url, body) {
  const b = body == null ? "" : typeof body === "string" ? body : JSON.stringify(body);
  return `${method.toUpperCase()} ${url} ${b}`;
}

// src/value/pricing.ts
var log = createLogger("fe-commons:pricing");
function priceRefKey(ref) {
  const chain = ref.chainId ? getChainMetadata(ref.chainId)?.slug ?? ref.chainId : "*";
  const id = (ref.address ?? ref.symbol ?? "?").toString().toLowerCase();
  return `${chain}:${id}`;
}
var StaticPriceSource = class {
  constructor(prices) {
    this.prices = prices;
  }
  prices;
  static {
    brandClass(this, "value/StaticPriceSource");
  }
  name = "static";
  async fetchPrice(ref) {
    const price = this.prices[priceRefKey(ref)] ?? (ref.symbol ? this.prices[`*:${ref.symbol.toLowerCase()}`] : void 0);
    if (price == null) return null;
    return { priceUsd: price, asOf: Date.now(), source: this.name };
  }
};
var PriceEngine = class {
  static {
    brandClass(this, "value/PriceEngine");
  }
  sources;
  cache;
  freshMs;
  pending = /* @__PURE__ */ new Map();
  /** Never-expiring last-known quotes, used when every source fails. */
  lastKnown = /* @__PURE__ */ new Map();
  constructor(opts) {
    this.sources = opts.sources;
    this.freshMs = opts.freshMs ?? 6e4;
    this.cache = opts.cache ?? new MemoryRequestCache({ defaultTtlMs: this.freshMs, maxEntries: 500 });
  }
  /** Resolve a single price (cached → sources in order → stale fallback → null). */
  getPrice(ref) {
    const key = priceRefKey(ref);
    const fresh = this.cache.get(key);
    if (fresh) return Promise.resolve(fresh);
    const inFlight = this.pending.get(key);
    if (inFlight) return inFlight;
    const p = this.resolveThroughSources(ref, key).finally(() => this.pending.delete(key));
    this.pending.set(key, p);
    return p;
  }
  /** Batch resolve. Returns a map keyed by `priceRefKey`. */
  async getPrices(refs) {
    const out = /* @__PURE__ */ new Map();
    await Promise.all(
      refs.map(async (ref) => {
        out.set(priceRefKey(ref), await this.getPrice(ref));
      })
    );
    return out;
  }
  /** Drop a cached quote (e.g. on manual refresh). */
  invalidate(ref) {
    this.cache.delete(priceRefKey(ref));
  }
  async resolveThroughSources(ref, key) {
    for (const source of this.sources) {
      try {
        const quote = await source.fetchPrice(ref);
        if (quote && Number.isFinite(quote.priceUsd)) {
          const resolved = { ...quote, source: quote.source || source.name };
          this.cache.set(key, resolved, this.freshMs);
          this.lastKnown.set(key, resolved);
          return resolved;
        }
      } catch (err) {
        log.warn(`source ${source.name} failed for ${key}`, { err: String(err) });
      }
    }
    const stale = this.lastKnown.get(key);
    if (stale) {
      log.debug(`serving stale price for ${key} (all sources failed)`);
      return { ...stale, stale: true };
    }
    return null;
  }
};
var BASE_UNITS_LABEL = "base units";
var UNSCALED_AMOUNT_NOTE = "Base units, unscaled: no decimals are known for this asset, so it is not scaled \u2014 a guessed scale would turn it into a quantity that reads as real.";
function knownScale(decimals) {
  return typeof decimals === "number" && Number.isInteger(decimals) && decimals >= 0;
}
function isUnscaledAmount(amount, decimals) {
  if (knownScale(decimals) || typeof amount === "number") return false;
  return typeof amount === "bigint" || /^[-+]?\d+$/.test(amount.trim());
}
function formatTokenAmount(amount, token = {}, opts = {}) {
  const { withSymbol = true, withUsd = false, compactUsd = false, maxFractionDigits = 6 } = opts;
  if (typeof amount !== "number" && !knownScale(token.decimals)) {
    const raw = typeof amount === "bigint" ? amount.toString() : amount.trim();
    const unit = withSymbol && token.symbol ? ` ${token.symbol}` : "";
    return isUnscaledAmount(raw, null) ? `${raw}${unit} ${BASE_UNITS_LABEL}` : `${raw}${unit}`;
  }
  const decimals = token.decimals ?? 0;
  const human = typeof amount === "number" ? (
    // Plain digits first: `String(1e-9)` is `"1e-9"`, which no trim below
    // recognises as a fraction and which reaches the screen as-is.
    trimFraction(toPlainDecimalString(amount), maxFractionDigits)
  ) : formatTokenDisplay(amount, decimals, { maxFractionDigits });
  let out = withSymbol && token.symbol ? `${human} ${token.symbol}` : human;
  if (withUsd && token.priceUsd != null) {
    const humanNum = typeof amount === "number" ? amount : Number(formatUnits(amount, decimals));
    out += ` (${formatUsd(humanNum * token.priceUsd, { compact: compactUsd })})`;
  }
  return out;
}
function trimFraction(value, maxFractionDigits) {
  if (!value.includes(".")) return value;
  const [w, f] = value.split(".");
  const trimmed = f.slice(0, maxFractionDigits).replace(/0+$/, "");
  if (trimmed) return `${w}.${trimmed}`;
  if (!isZeroText(w) || /[1-9]/.test(f ?? "")) {
    return formatTokenDisplay(parseUnits(value, f.length), f.length, {
      maxFractionDigits
    });
  }
  return w;
}
function nativeTokenMeta(chainId) {
  const chain = getChainMetadata(chainId);
  return chain ? { symbol: chain.nativeToken, decimals: chain.decimals } : {};
}
var NATIVE_UNITS_LABEL = "native units";
function nativeUnitLabel(chainId) {
  return nativeTokenMeta(chainId).symbol ?? NATIVE_UNITS_LABEL;
}

// src/display/truncation.ts
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var truncation = globalSingleton("display/truncation", () => ({
  active: DEFAULT_TRUNCATION
}));
function configureTruncation(partial) {
  const current = truncation.active;
  truncation.active = {
    hash: { ...current.hash, ...partial.hash },
    address: { ...current.address, ...partial.address }
  };
}
function getTruncation(kind) {
  const { active } = truncation;
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text2 = value.trim();
  if (/\s/.test(text2)) return value;
  const prefix = hexPrefixOf(text2);
  const body = text2.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text2;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}
function resetTruncationConfig() {
  truncation.active = DEFAULT_TRUNCATION;
}
var LONG_HEX_RUN = /0x[0-9a-fA-F]{24,}/g;
function shortenIdentifiersIn(text2) {
  return text2.replace(LONG_HEX_RUN, (match) => {
    const kind = match.length > 42 ? "hash" : "address";
    return truncateMiddle(match, getTruncation(kind));
  });
}
var EvidenceSourceSchema = z.object({ "kind": z.enum(["trace_opcode", "registry", "call_shape", "abi", "proxy_abi", "wire_layout", "provided", "generic", "heuristic", "effects", "provider", "user", "automation", "model", "unknown", "aggregate"]).describe("How / by whom an :class:`Evidence` assertion was produced.\n\nThe decode ``method`` values (``abi`` \u2026 ``generic``) and the catalog origin\nvalues (``provider`` / ``user`` / ``automation``) live in one enum so the\nscoring engine can price any producer from a single ``method_base`` table.").default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.enum(["internal", "external"]).describe('Whether an evidence source\'s IDENTITY may be shown to external customers.\n\nConfidence, lifecycle and the fact that a row is corroborated are always\nfair game \u2014 that is the "defensible answer". This governs only the *who*:\n\n- ``INTERNAL`` \u2014 a third-party source we scrape / ingest. External audiences\n  see it collapsed to a neutral first-party rollup\n  (:data:`EXTERNAL_SOURCE_LABEL`), so a customer never learns we source from\n  that provider.\n- ``EXTERNAL`` \u2014 a human operator / first-party / registry / decode\n  assertion whose *kind* is safe to surface (its raw ids/urls are still\n  dropped on redaction).'), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).');
var EvidenceSchema = z.object({ "source": z.object({ "kind": z.enum(["trace_opcode", "registry", "call_shape", "abi", "proxy_abi", "wire_layout", "provided", "generic", "heuristic", "effects", "provider", "user", "automation", "model", "unknown", "aggregate"]).describe("How / by whom an :class:`Evidence` assertion was produced.\n\nThe decode ``method`` values (``abi`` \u2026 ``generic``) and the catalog origin\nvalues (``provider`` / ``user`` / ``automation``) live in one enum so the\nscoring engine can price any producer from a single ``method_base`` table.").default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.enum(["internal", "external"]).describe('Whether an evidence source\'s IDENTITY may be shown to external customers.\n\nConfidence, lifecycle and the fact that a row is corroborated are always\nfair game \u2014 that is the "defensible answer". This governs only the *who*:\n\n- ``INTERNAL`` \u2014 a third-party source we scrape / ingest. External audiences\n  see it collapsed to a neutral first-party rollup\n  (:data:`EXTERNAL_SOURCE_LABEL`), so a customer never learns we source from\n  that provider.\n- ``EXTERNAL`` \u2014 a human operator / first-party / registry / decode\n  assertion whose *kind* is safe to surface (its raw ids/urls are still\n  dropped on redaction).'), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).').optional(), "confidence": z.number().gte(0).lte(1).default(1), "reasons": z.array(z.string()).optional(), "signals": z.record(z.string(), z.any()).optional(), "asserted_fields": z.array(z.string()).optional(), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "context": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One assertion: *this source claims X, with this confidence, because \u2026*.\n\nReplaces both ``SemanticEvidence`` (decode) and ``ProviderAttribution``\n(catalog). ``confidence`` is clamped to ``[0, 1]``. ``asserted_at`` /\n``last_seen_at`` default to wall-clock seconds so a fresh assertion merges\nwith an older one without a ``None`` branch (see\n:func:`commons.evidence.engine.merge_evidence`).\n\nDecode-specific coordinates (chain / block / cross-chain hop / proxy\ntraversal) live in :attr:`context` under the ``CTX_*`` keys; read them via\nthe convenience properties (:attr:`chain_id`, :attr:`dest_chain_id`, \u2026).");

// src/models/wire/graph-scene.ts
var MEDIA_REF_KINDS = ["placeholder", "cdn_key", "url"];
var GRAPH_KINDS = ["semantic", "flow", "interaction", "knowledge"];
var GRAPH_LAYOUT_HINTS = ["hierarchical", "temporal", "force"];
var SCENE_NODE_KINDS = [
  "address",
  "contract",
  "entity",
  "cluster",
  "exchange",
  "defi",
  "token",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "message",
  "settlement"
];
var OFFCHAIN_SCENE_NODE_KINDS = [
  "entity",
  "cluster",
  "exchange",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "settlement"
];
var SCENE_EDGE_KINDS = [
  "approval",
  "transfer",
  "call",
  "contains",
  "bridge_out",
  "bridge_in",
  "cross_chain",
  "flow",
  "verify",
  "creates"
];
var SCENE_GROUP_TYPES = ["chain", "protocol", "cluster"];
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var SCENE_EMPTY_REASONS = [
  "no_semantic_tree",
  "decluttered_empty",
  "no_flow_source",
  "no_value_moved",
  "new_issuance",
  "shielded",
  "pooled",
  "relocked"
];
var SCENE_EMPTY_REASON_ATTR = "empty_reason";
var SCENE_ELEMENT_ORIGINS = ["decoded", "synthesized", "user"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_id: z.union([z.string(), z.null()]).default(null),
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  max_amount_raw: z.union([z.string(), z.null()]).optional(),
  max_amount: z.union([z.string(), z.null()]).optional(),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  // The well-known asset this token CLAIMS to be and is not (see
  // `commons.tokens.impersonation`). Its `symbol` stays what it calls itself;
  // a renderer must not show that bare. Absent for an honest token.
  impersonates: z.union([z.record(z.string(), z.unknown()), z.null()]).optional()
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  semantic_node_id: z.union([z.string(), z.null()]).default(null),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var MERGED_COUNT_ATTR = "merged_count";
var MERGED_TXS_ATTR = "merged_txs";
var MERGED_TXS_LIMIT = 100;
function mergedTxRef(parts) {
  const id = parts.canonicalTxId || parts.txHash;
  if (!id) return void 0;
  return {
    id,
    ...parts.txHash ? { tx_hash: parts.txHash } : {},
    ...parts.chainId ? { chain_id: parts.chainId } : {}
  };
}
function mergedTxRefs(attributes) {
  return parseTxRefs(attributes?.[MERGED_TXS_ATTR]);
}
var TX_MEMBERSHIP_ATTR = "in_txs";
function txMembership(attributes) {
  return parseTxRefs(attributes?.[TX_MEMBERSHIP_ATTR]);
}
function groupSceneLegs(attributes) {
  const raw = attributes?.legs;
  if (!Array.isArray(raw)) return [];
  const legs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, role, chain_id, tx_hash } = entry;
    if (typeof id !== "string" || !id) continue;
    legs.push({
      id,
      index: legs.length,
      ...typeof role === "string" && role ? { role } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {}
    });
  }
  return legs;
}
function parseTxRefs(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const refs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, tx_hash, chain_id, amount_raw } = entry;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    refs.push({
      id,
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof amount_raw === "string" && /^-?\d+$/.test(amount_raw) ? { amount_raw } : {}
    });
  }
  return refs;
}
var TX_COUNT_ATTR = "tx_count";
function txCountSublabel(count) {
  return `${count} tx`;
}
function isTxCountSublabel(sublabel) {
  return sublabel == null || sublabel === "" || /^\d+ tx$/.test(sublabel);
}
function mergedCount(attributes) {
  const value = attributes?.[MERGED_COUNT_ATTR];
  return typeof value === "number" && value > 0 ? value : 1;
}
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var GraphSceneSchema = z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]).transform((edges) => {
    const reserved = new Set(edges.map((edge) => edge.id));
    const seen = /* @__PURE__ */ new Set();
    return edges.map((edge) => {
      if (!seen.has(edge.id)) {
        seen.add(edge.id);
        return edge;
      }
      let suffix = 2;
      let id = `${edge.id}:duplicate:${suffix}`;
      while (reserved.has(id)) id = `${edge.id}:duplicate:${++suffix}`;
      reserved.add(id);
      return { ...edge, id };
    });
  }),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
function parseGraphScene(value) {
  return GraphSceneSchema.parse(value);
}
var SceneOverlaySchema = z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var AUTHORED_BY_ATTR = "authored_by";
var AUTHORED_AT_ATTR = "authored_at";
var AUTHORED_REASON_ATTR = "authored_reason";
var AUTHORED_SCOPE_ATTR = "authored_scope";
var CONTESTED_ATTR = "contested";
function emptySceneOverlay() {
  return {
    nodes: [],
    edges: [],
    groups: [],
    hidden_node_ids: [],
    hidden_edge_ids: [],
    node_patches: {},
    edge_patches: {},
    positions: {},
    authored_by: null,
    authored_at: null,
    authored_reason: null,
    authored_scope: null
  };
}
var SUBJECT_ANY = "*";
var OVERLAY_BODY_KEYS = /* @__PURE__ */ new Set([
  "nodes",
  "edges",
  "groups",
  "hidden_node_ids",
  "hidden_edge_ids",
  "node_patches",
  "edge_patches",
  "positions",
  AUTHORED_BY_ATTR,
  AUTHORED_AT_ATTR,
  AUTHORED_REASON_ATTR,
  AUTHORED_SCOPE_ATTR
]);
function isSceneOverlayBody(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return Object.keys(value).some((key) => OVERLAY_BODY_KEYS.has(key));
}
function annotationLayers(annotations, subjectKey) {
  const read = (value) => {
    try {
      return parseSceneOverlay(value);
    } catch {
      return emptySceneOverlay();
    }
  };
  if (isSceneOverlayBody(annotations)) return { [subjectKey]: read(annotations) };
  if (typeof annotations !== "object" || annotations === null) return {};
  const out = {};
  for (const [key, value] of Object.entries(annotations)) {
    if (typeof value === "object" && value !== null) out[key] = read(value);
  }
  return out;
}
function mergeAnnotationLayer(annotations, subjectKey, overlay) {
  return { ...annotationLayers(annotations, subjectKey), [subjectKey]: overlay };
}
function layerForSubject(annotations, subjectKey) {
  return annotationLayers(annotations, subjectKey)[subjectKey] ?? emptySceneOverlay();
}
function parseSceneOverlay(value) {
  return SceneOverlaySchema.parse(value);
}
var GROUPING_DIMENSIONS = [
  "holder_share",
  "related",
  "hop",
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "catalog_group",
  "kind",
  "saga",
  "transaction"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
var GroupingSpecSchema = z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function defaultGroupingSpec() {
  return { dimensions: ["chain"] };
}
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key, opts] of Object.entries(raw.options)) {
      if (!known.has(key)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}

// src/display/coerce.ts
function str(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function asString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
function asTrimmedString(value) {
  const text2 = typeof value === "string" ? value.trim() : "";
  return text2 === "" ? void 0 : text2;
}
function text(value) {
  return value == null ? "" : String(value);
}
function num(value) {
  return coerceNumber(value);
}

// src/display/chainText.ts
var UNSAFE_CHARS = /[\p{Default_Ignorable_Code_Point}\u200B-\u200F\u061C\u202A-\u202E\u2060\u2066-\u2069\uFEFF\u00AD]/gu;
var WHITESPACE_RUN = /\s+/g;
var CHAIN_TEXT_MAX_CHARS = 64;
var CHAIN_TICKER_MAX_CHARS = 16;
function chainText(value, opts = {}) {
  const cleaned = String(value ?? "").replace(UNSAFE_CHARS, "").replace(WHITESPACE_RUN, " ").trim();
  const maxChars = opts.maxChars ?? CHAIN_TEXT_MAX_CHARS;
  if (cleaned.length <= maxChars) {
    return { text: cleaned, title: cleaned, truncated: false };
  }
  return { text: `${cleaned.slice(0, maxChars)}\u2026`, title: cleaned, truncated: true };
}
function safeChainText(value, opts = {}) {
  return chainText(value, opts).text;
}
function displayChainText(value, opts = {}) {
  const cleaned = chainText(value, { maxChars: Number.POSITIVE_INFINITY });
  const shortened = shortenIdentifiersIn(shortenLongNumbers(cleaned.text));
  return { text: chainText(shortened, opts).text, title: cleaned.title };
}
function safeTicker(value) {
  return chainText(value, { maxChars: CHAIN_TICKER_MAX_CHARS });
}

// src/display/nodeKinds.ts
var GRAPH_NODE_KINDS = [
  "eoa",
  "contract",
  "entity",
  "cluster",
  "tx",
  "exchange",
  "defi",
  "token",
  "message",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "verification",
  "relayer",
  "settlement",
  "situation"
];
var NODE_KIND_META = {
  eoa: { label: "Wallet", authorable: true },
  contract: { label: "Contract", authorable: true },
  entity: { label: "Entity", authorable: true },
  cluster: { label: "Cluster" },
  tx: { label: "Transaction" },
  exchange: { label: "Exchange", badge: "CEX", authorable: true },
  defi: { label: "DeFi protocol", badge: "DeFi", authorable: true },
  token: { label: "Token", badge: "Token", authorable: true },
  // A cross-chain message payload (e.g. a Tokemak message riding CCIP), anchored
  // on-chain under the recipient. On-chain (so it stays inside its chain
  // rectangle) but reads as a structured protocol artifact, not a wallet.
  message: { label: "Message", badge: "Message" },
  oracle: { label: "Oracle", badge: "Oracle", offChain: true, authorable: true },
  bridge: { label: "Bridge", badge: "Bridge", offChain: true, authorable: true },
  don: { label: "Oracle network", badge: "DON", offChain: true },
  mixer: { label: "Mixer", badge: "Mixer", offChain: true, authorable: true },
  // A cross-chain verification/attestation step (CCIP's Committee Verifier +
  // Executor). Off-chain infra like the DON it hangs under.
  verification: { label: "Verification", badge: "Verified", offChain: true },
  // An off-chain message-delivery relayer / executor (Wormhole Executor,
  // LayerZero executor, Across relayer). Distinct from the bridge *hub* it
  // serves — it carries the message after attestation.
  relayer: { label: "Relayer", badge: "Relayer", offChain: true },
  // The off-chain venue that fills a posted order — a CoW solver batch auction,
  // a Fusion/UniswapX filler network. Like a DON it is a set of competing agents
  // the transaction hands work to, not a route value travels along.
  settlement: { label: "Settlement venue", badge: "Settlement", offChain: true },
  // An analyst's incident/hypothesis, not an engine-confirmed finding. Chain
  // context is allowed, but a situation is not an account or a value movement.
  situation: { label: "Situation (reported incident)", badge: "Reported situation", offChain: true, authorable: true }
};
var SCENE_KIND_TO_DOMAIN_KIND = {
  // On-chain.
  address: "eoa",
  eoa: "eoa",
  wallet: "eoa",
  contract: "contract",
  defi: "defi",
  token: "token",
  message: "message",
  tx: "tx",
  // Resolved catalog entities.
  entity: "entity",
  cluster: "cluster",
  exchange: "exchange",
  cex: "exchange",
  // Off-chain infrastructure.
  oracle: "oracle",
  bridge: "bridge",
  don: "don",
  mixer: "mixer",
  virtual: "entity",
  verification: "verification",
  attestation: "verification",
  settlement: "settlement",
  relayer: "relayer",
  situation: "situation"
};
function domainKindForSceneKind(kind, opts = {}) {
  if (kind) {
    const mapped = SCENE_KIND_TO_DOMAIN_KIND[kind];
    if (mapped) return mapped;
  }
  if (opts.offChain) return "entity";
  return void 0;
}
function labelForKind(kind) {
  return kind ? NODE_KIND_META[kind].label : void 0;
}
function badgeForKind(kind) {
  return kind ? NODE_KIND_META[kind].badge : void 0;
}
function isOffChainKind(kind) {
  return kind != null && NODE_KIND_META[kind].offChain === true;
}
function nodeKindLabel(kind, opts = {}) {
  const domain = domainKindForSceneKind(kind, opts);
  if (domain) return NODE_KIND_META[domain].label;
  if (!kind) return void 0;
  const words = kind.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : kind;
}
var AUTHORABLE_NODE_KINDS = GRAPH_NODE_KINDS.filter((k) => NODE_KIND_META[k].authorable).map((kind) => ({
  kind,
  label: NODE_KIND_META[kind].label
}));

// src/display/index.ts
var TX_FIELDS = {
  canonicalTxId: "canonical_tx_id",
  txHash: "tx_hash",
  chainId: "chain_id",
  blockNumber: "block_number",
  blockHash: "block_hash",
  blockTimestamp: "block_timestamp",
  transactionIndex: "transaction_index",
  type: "type",
  finality: "finality",
  direction: "direction",
  status: "status",
  totalValueRaw: "total_value_raw",
  amountHidden: "amount_hidden",
  valueUsd: "value_usd",
  primaryEntityId: "primary_entity_id",
  // `primaryProtocolId` resolves to whatever standard the TOP-LEVEL call matched,
  // so it is frequently a token standard (`erc721`) while the protocol the row is
  // interesting for sits in `protocolIds` alongside it. Surfaces that answer
  // "which protocol is this?" should read the set, not the primary alone.
  primaryProtocolId: "primary_protocol_id",
  protocolIds: "protocol_ids",
  from: "from",
  to: "to",
  counts: "counts",
  createdAt: "created_at",
  updatedAt: "updated_at",
  // Decode glass-box, three independent questions. `decodeConfidence` grades the
  // SEMANTIC reading ("this is a bridge send"); `decodeCoverage`/`decodeCounts`
  // say how much of the tx that reading covers; `abiSource`/`abiConfidence` state
  // the MECHANICAL provenance of the top-level call's ABI. A row can be
  // semantically confident, thinly covered and mechanically weak at once, so they
  // stay separate fields rather than collapsing into one "quality" number.
  decodeConfidence: "decode_confidence",
  decodeCoverage: "decode_coverage",
  decodeCounts: "decode_counts",
  explanation: "explanation",
  abiSource: "abi_source",
  abiConfidence: "abi_confidence",
  // The mechanical half of the "why": which tenancy rung's ABI decoded the
  // top-level call. Separate from `explanation`, which explains the semantic
  // reading — a row can be read correctly by an ABI the operator did not expect
  // to win, and only these bullets name the rung.
  abiExplanation: "abi_explanation",
  // WHICH PIPELINE produced this copy. A fourth independent question, and the
  // one that frames the other three: "coverage 100%" is true of whatever
  // pipeline ran, and the cheapest tier reaches it by not looking. The server
  // decides `belowDefault` — never the most-complete tier, which would flag
  // almost every row — so no client re-derives which tier counts as thin.
  pipelineTier: "pipeline_tier",
  pipelineTierLabel: "pipeline_tier_label",
  pipelineTierBelowDefault: "pipeline_tier_below_default"
};
var FIELD_LABELS = {
  // transaction
  canonical_tx_id: "Transaction ID",
  tx_hash: "Tx Hash",
  chain_id: "Chain",
  block_number: "Block",
  block_hash: "Block Hash",
  block_timestamp: "Time",
  transaction_index: "Index",
  type: "Type",
  finality: "Finality",
  direction: "Direction",
  status: "Status",
  total_value_raw: "Value",
  amount_hidden: "Amount hidden",
  value_usd: "Value (USD)",
  // Distinct from the `entity` catalog label below, which is the RESOLVED entity
  // (a named chip from the precedence engine). This is the raw id the row was
  // stamped with at ingest, and the two can appear side by side — a column
  // menu offering "Entity" twice is a menu that can't be used.
  primary_entity_id: "Entity ID",
  primary_protocol_id: "Primary protocol",
  protocol_ids: "Protocols",
  from: "From",
  to: "To",
  counts: "Counts",
  created_at: "Created",
  updated_at: "Updated",
  decode_confidence: "Decode confidence",
  decode_coverage: "Decode coverage",
  decode_counts: "Decode coverage detail",
  abi_source: "ABI source",
  abi_confidence: "ABI confidence",
  abi_explanation: "ABI provenance",
  pipeline_tier_label: "Pipeline",
  // canonical-tx attr leaves (shared with `@authe/fe-commons/triggers`, which
  // resolves its field-path labels through `fieldLabel` so the rule builder and
  // the transaction table can never disagree on a name).
  from_address: "From",
  to_address: "To",
  value: "Value (native)",
  input_data: "Input Data",
  total_usd: "Value (USD)",
  // enrichment payload leaves (trigger DSL `<kind>.<path>`)
  score: "Risk Score",
  bucket: "Risk Bucket",
  policy_id: "Risk Policy",
  catalog_id: "Catalog ID",
  kind: "Kind",
  // address / catalog
  address: "Address",
  label: "Label",
  tag: "Tag",
  entity: "Entity",
  group: "Group",
  protocol: "Protocol",
  // A machine cross-chain saga (a bridge's legs tied by the protocol's own
  // correlation id) — labelled by what it shows rather than by the internal
  // "saga" term, which means nothing to an investigator.
  // What the transaction DID, as the decode read it — "Approve", "Mint
  // Signed", "Deposit Native 0.01 ETH". Distinct from `type`, which is the
  // classification ("transaction") and says nothing about the action.
  tx_action: "Action",
  // WHICH assets moved, as opposed to `value`'s how-much-in-native-and-USD.
  // An NFT purchase reads in `value` as the ETH that left it; the token it
  // bought only appears here.
  assets: "Assets",
  saga: "Cross-chain",
  // The correlation id itself, as opposed to `saga`'s "did every leg arrive".
  // Generic because the protocol names it: a CCIP message id, a Wormhole VAA.
  cross_chain_id: "Cross-chain ID",
  // Catalog attribution comes in two subjects that are easy to confuse and were:
  // what someone said about THIS TRANSFER, versus what they said about the WALLET
  // on the other end. Both label sets are called "labels" by their own API, so
  // the column headers are where the distinction has to be made visible.
  tx_labels: "Labels",
  tx_tags: "Tags",
  tx_groups: "Groups",
  tx_entity: "Entity",
  // The TRANSACTION's own risk verdict (the pipeline's score on the row), as
  // opposed to `counterparty_risk`, which is what is known about the ADDRESS on
  // the other end. Same pairing as the attribution columns: the unqualified name
  // is the transaction's.
  tx_risk: "Risk",
  counterparty_labels: "Counterparty labels",
  counterparty_tags: "Counterparty tags",
  counterparty_groups: "Counterparty groups",
  counterparty_entity: "Counterparty entity",
  counterparty_risk: "Counterparty risk",
  row_flags: "Flags",
  // The SENDING address's attribution — the same claim as `counterparty_*` about
  // the row's other end. Named by role rather than by position for the same
  // reason `counterparty_*` is: a header reading "From labels" would be read as
  // "labels that came from somewhere".
  sender_labels: "Sender labels",
  sender_tags: "Sender tags",
  sender_groups: "Sender groups",
  sender_entity: "Sender entity",
  notes: "Note",
  triage: "Triage",
  risk_score: "Risk",
  // catalog rows (entities / tags / labels / groups — canonical commons fields)
  name: "Name",
  description: "Description",
  entity_type: "Type",
  risk_level: "Risk Level",
  website: "Website",
  twitter: "Twitter",
  category: "Category",
  color: "Colour",
  icon: "Icon",
  logo_s3_uri: "Logo",
  lifecycle: "Lifecycle",
  severity: "Severity",
  aliases: "Aliases",
  chain: "Chain",
  parent_entity_id: "Parent Entity",
  related_entity_ids: "Related Entities",
  member_tag_ids: "Tags",
  member_label_ids: "Labels",
  member_entity_ids: "Entities",
  system_default: "System Default",
  confidence: "Confidence",
  explanation: "Why",
  source: "Source",
  is_human_approved: "Approved",
  // tenancy
  org_id: "Organization",
  tenant_id: "Tenant",
  customer_id: "Customer",
  user_id: "User"
};
var IDENTITY_NAME_FALLBACK = ["display_name", "name", "slug"];
function entityDisplayName(source, opts = {}) {
  const fields = opts.fields ?? IDENTITY_NAME_FALLBACK;
  if (source) {
    for (const field of fields) {
      const value = source[field];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  const id = opts.fallbackId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}
function titleCase(key) {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
function fieldLabel(key) {
  return FIELD_LABELS[key] ?? titleCase(key);
}
function fieldLabels(keys, overrides) {
  const out = {};
  for (const key of keys) out[key] = fieldLabel(key);
  return overrides ? { ...out, ...overrides } : out;
}
var IRREGULAR_PLURALS = {
  person: "people",
  child: "children",
  alias: "aliases",
  status: "statuses"
};
function applyLeadingCase(source, target) {
  const first = source.charAt(0);
  if (first && first === first.toUpperCase() && first !== first.toLowerCase()) {
    return target.charAt(0).toUpperCase() + target.slice(1);
  }
  return target;
}
function pluralize(word, count) {
  if (count === 1) return word;
  const trimmed = word.trim();
  if (!trimmed) return word;
  const irregular = IRREGULAR_PLURALS[trimmed.toLowerCase()];
  if (irregular) return applyLeadingCase(trimmed, irregular);
  if (/[^aeiou]y$/i.test(trimmed)) return `${trimmed.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(trimmed)) return `${trimmed}es`;
  return `${trimmed}s`;
}
var ARTICLE_EXCEPTIONS = {
  // Consonant sound behind a vowel letter.
  user: "a",
  url: "a",
  uuid: "a",
  unit: "a",
  union: "a",
  // Vowel sound behind a consonant letter.
  hour: "an",
  honest: "an"
};
function indefiniteArticle(noun, capitalize = false) {
  const first = (noun.trim().split(/[\s-]/)[0] ?? "").toLowerCase();
  const article = ARTICLE_EXCEPTIONS[first] ?? (/^[aeiou]/.test(first) ? "an" : "a");
  return capitalize ? `${article[0].toUpperCase()}${article.slice(1)}` : article;
}
function withArticle(noun, capitalize = false) {
  return `${indefiniteArticle(noun, capitalize)} ${noun}`;
}
function pluralizeLabel(key, count) {
  return pluralize(fieldLabel(key), count);
}
function countLabel(count, word, display) {
  return `${display ?? count} ${pluralize(word, count)}`;
}
var GRAPH_VIEW_MODES = ["hierarchical", "temporal", "force"];
var GRAPH_VIEW_MODE_LABELS = {
  hierarchical: "Hierarchical",
  temporal: "Timeline",
  force: "Force"
};
var GRAPH_FLOW_VIEW_MODES = GRAPH_VIEW_MODES.filter(
  (mode) => mode !== "force"
);
function resolveGraphViewMode(stored, offered = GRAPH_VIEW_MODES) {
  if (stored && offered.includes(stored)) return stored;
  return offered[0] ?? "hierarchical";
}
var GRAPH_DETAIL_TIERS = SCENE_DETAIL_TIERS;
var GRAPH_DETAIL_TIER_LABELS = {
  compact: "Compact",
  standard: "Standard",
  expanded: "Expanded"
};
var DEFAULT_GRAPH_DETAIL_TIER = "compact";
var GRAPH_PANEL_KEYS = ["legend", "highlights", "stats", "minimap"];
var GRAPH_PANEL_LABELS = {
  legend: "Legend",
  highlights: "Transaction highlights",
  stats: "Chain stats",
  minimap: "Minimap"
};
var ACTIVITY_VIEWS = ["table", "graph"];
var ACTIVITY_VIEW_LABELS = {
  table: "Table",
  graph: "Graph"
};
var LIVE_MODES = ["live", "interval", "off"];
var LIVE_MODE_LABELS = {
  live: "Live",
  interval: "Auto-refresh",
  off: "Off"
};
var DEFAULT_LIVE_REFRESH_MS = 15e3;
function liveModesFor(capabilities) {
  const modes = [];
  if (capabilities.serverStreaming) modes.push("live");
  if (capabilities.autoRefresh !== false) modes.push("interval");
  modes.push("off");
  return modes;
}
function allowedLiveMode(mode, modes) {
  if (modes.includes(mode)) return mode;
  const fallback = LIVE_MODES.slice(LIVE_MODES.indexOf(mode) + 1).find((m) => modes.includes(m));
  return fallback ?? modes[modes.length - 1] ?? "off";
}
var TABLE_DENSITIES = ["comfortable", "normal", "compact"];
var DEFAULT_TABLE_DENSITY = "comfortable";
var TABLE_DENSITY_LABELS = {
  comfortable: "Comfortable",
  normal: "Normal",
  compact: "Compact"
};
var TABLE_DENSITY_ROW_HEIGHT = {
  comfortable: 44,
  normal: 38,
  compact: 32
};
function resolveDensity(input) {
  return input.override ?? input.stored ?? input.fallback ?? DEFAULT_TABLE_DENSITY;
}
function sentenceList(items, type = "conjunction") {
  const parts = items.filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  const ListFormat = Intl.ListFormat;
  if (ListFormat) return new ListFormat(void 0, { style: "long", type }).format(parts);
  const word = type === "disjunction" ? "or" : "and";
  return `${parts.slice(0, -1).join(", ")} ${word} ${parts[parts.length - 1]}`;
}
function shortId(id) {
  if (id.length <= 18) return id;
  return truncateMiddle(id, { head: 10, tail: 4 });
}
function idTail(targetId) {
  if (!targetId) return "";
  const last = targetId.split("/").filter(Boolean).pop() ?? targetId;
  return last.length > 12 ? `${ELLIPSIS}${last.slice(-8)}` : last;
}
function initials(source, max = 2) {
  const parts = (source ?? "").trim().split(/[\s._-]+/).map((part) => part.replace(/^[^\p{L}\p{N}]+/u, "")).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, max).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
var DISPLAY_PREF_KEYS = {
  tableDensity: "display:table-density",
  columnVisibility: "display:columns",
  regionAliasPin: "display:region-alias-pin",
  graphViewMode: "display:graph-view-mode",
  graphDetailTier: "display:graph-detail-tier",
  graphAssetColors: "display:graph-asset-colors",
  graphHiddenPanels: "display:graph-panels",
  graphGrouping: "display:graph-grouping",
  activityView: "display:activity-view",
  liveMode: "display:live-mode",
  inspector: "display:inspector",
  attributionChips: "display:attribution-chips",
  quickActions: "display:quick-actions"
};
var QUICK_ACTION_VISIBILITIES = ["auto", "always", "hover"];
var ATTRIBUTION_KINDS = ["entity", "label", "tag", "group"];
var DEFAULT_ATTRIBUTION_CHIP_PREFS = {
  kinds: ATTRIBUTION_KINDS,
  max: 3,
  logos: true,
  size: "sm",
  showProvenance: false,
  showWhy: true
};
function resolveAttributionChipPrefs(override, userPref) {
  return {
    ...DEFAULT_ATTRIBUTION_CHIP_PREFS,
    ...stripUndefined(userPref),
    ...stripUndefined(override)
  };
}
function stripUndefined(prefs) {
  if (!prefs) return {};
  return Object.fromEntries(
    Object.entries(prefs).filter(([, value]) => value !== void 0)
  );
}
var DEFAULT_REGION_ALIAS_PIN = "default";
function coerceEnum(value, allowed, fallback) {
  return typeof value === "string" && allowed.includes(value) ? value : fallback;
}
var DisplayPrefsStore = class _DisplayPrefsStore {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  static {
    brandClass(this, "display/DisplayPrefsStore");
  }
  /** Derive a store for a different identity (e.g. on tenant switch). */
  with(patch) {
    return new _DisplayPrefsStore(this.storage.with(patch));
  }
  getTableDensity(fallback = "comfortable") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.tableDensity, fallback),
      TABLE_DENSITIES,
      fallback
    );
  }
  setTableDensity(density, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.tableDensity, density, level);
  }
  getGraphAssetColors(fallback = true) {
    const value = this.storage.get("display:graph-asset-colors", fallback);
    return typeof value === "boolean" ? value : fallback;
  }
  setGraphAssetColors(enabled, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphAssetColors, enabled, level);
  }
  getGraphViewMode(fallback = "hierarchical") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphViewMode, fallback),
      GRAPH_VIEW_MODES,
      fallback
    );
  }
  setGraphViewMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphViewMode, mode, level);
  }
  getGraphDetailTier(fallback = DEFAULT_GRAPH_DETAIL_TIER) {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphDetailTier, fallback),
      GRAPH_DETAIL_TIERS,
      fallback
    );
  }
  setGraphDetailTier(tier, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphDetailTier, tier, level);
  }
  getActivityView(fallback = "table") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.activityView, fallback),
      ACTIVITY_VIEWS,
      fallback
    );
  }
  setActivityView(view, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.activityView, view, level);
  }
  getLiveMode(fallback = "interval") {
    return coerceEnum(this.storage.get(DISPLAY_PREF_KEYS.liveMode, fallback), LIVE_MODES, fallback);
  }
  setLiveMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.liveMode, mode, level);
  }
  /** See {@link DisplayPrefs.inspector}. Anything non-boolean reads as the default. */
  getInspector(fallback = true) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.inspector, fallback);
    return typeof value === "boolean" ? value : fallback;
  }
  setInspector(enabled, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.inspector, enabled, level);
  }
  /**
   * See {@link DisplayPrefs.attributionChips}. Returns only the fields the user
   * has actually expressed, so the caller can layer it under a surface override
   * without a stored default silently outranking one.
   */
  getAttributionChips() {
    const raw = this.storage.get(DISPLAY_PREF_KEYS.attributionChips, {});
    if (!raw || typeof raw !== "object") return {};
    const stored = raw;
    const out = {};
    if (Array.isArray(stored.kinds)) {
      const kinds = stored.kinds.filter(
        (k) => ATTRIBUTION_KINDS.includes(k)
      );
      if (kinds.length > 0) out.kinds = kinds;
    }
    if (typeof stored.max === "number" && Number.isFinite(stored.max) && stored.max >= 0) {
      out.max = Math.floor(stored.max);
    }
    if (typeof stored.logos === "boolean") out.logos = stored.logos;
    if (stored.size === "sm" || stored.size === "md") out.size = stored.size;
    if (typeof stored.showProvenance === "boolean") out.showProvenance = stored.showProvenance;
    return out;
  }
  setAttributionChips(prefs, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.attributionChips, prefs, level);
  }
  /**
   * See {@link DisplayPrefs.quickActions}. `undefined` when the reader has not
   * chosen, or chose `auto` — both mean "each surface decides", and reporting
   * `auto` as an expressed choice would let it outrank a surface's own default.
   */
  getQuickActions() {
    const value = this.storage.get(DISPLAY_PREF_KEYS.quickActions, null);
    return value === "always" || value === "hover" ? value : void 0;
  }
  /** `auto` (or `undefined`) forgets the choice, so the surfaces decide again. */
  setQuickActions(value, level) {
    if (!value || value === "auto") {
      this.storage.remove(DISPLAY_PREF_KEYS.quickActions, level);
      return true;
    }
    return this.storage.set(DISPLAY_PREF_KEYS.quickActions, value, level);
  }
  getRegionAliasPin(fallback = DEFAULT_REGION_ALIAS_PIN) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.regionAliasPin, fallback);
    return typeof value === "string" ? value : fallback;
  }
  setRegionAliasPin(alias, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.regionAliasPin, alias, level);
  }
  /**
   * Overlay panels the user dismissed on one graph surface.
   *
   * Keyed per graph (as column visibility is keyed per table) because dismissing
   * the legend on a dense case graph shouldn't hide it on a two-node transaction
   * graph. Stores the DISMISSED set, not the visible one, so a panel added later
   * shows up by default instead of being retroactively hidden by an older pref.
   * Unknown keys are dropped on read.
   */
  getGraphHiddenPanels(graphId, fallback = []) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`,
      fallback
    );
    if (!Array.isArray(stored)) return fallback;
    return stored.filter(
      (key) => GRAPH_PANEL_KEYS.includes(key)
    );
  }
  setGraphHiddenPanels(graphId, hidden, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`, [...hidden], level);
  }
  /**
   * How the user chose to band one graph surface (the "Group by" order).
   *
   * Keyed per graph for the same reason the panels are: grouping a case file by
   * entity says nothing about how a single transaction should be drawn. Parsed
   * on read, so a dimension this build no longer knows is dropped rather than
   * breaking the graph.
   */
  getGraphGrouping(graphId, fallback = null) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`,
      void 0
    );
    return parseGroupingSpec(stored) ?? fallback;
  }
  setGraphGrouping(graphId, grouping, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`, grouping, level);
  }
  /** Column visibility for one table id (`{ [columnId]: visible }`). */
  getColumnVisibility(tableId, fallback = {}) {
    return this.storage.get(
      `${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`,
      fallback
    );
  }
  setColumnVisibility(tableId, visibility, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`, visibility, level);
  }
  /** Load the non-table-scoped prefs as one bag (column visibility is per-table). */
  load() {
    return {
      tableDensity: this.getTableDensity(),
      regionAliasPin: this.getRegionAliasPin(),
      graphViewMode: this.getGraphViewMode(),
      graphDetailTier: this.getGraphDetailTier(),
      graphAssetColors: this.getGraphAssetColors(),
      activityView: this.getActivityView(),
      liveMode: this.getLiveMode(),
      inspector: this.getInspector(),
      attributionChips: this.getAttributionChips(),
      ...this.getQuickActions() ? { quickActions: this.getQuickActions() } : {}
    };
  }
  /**
   * Persist whichever prefs are present (skips `undefined` fields).
   *
   * `true` only when every field it actually wrote persisted — same contract as
   * the individual setters, so a bulk save cannot claim more than they do.
   * Every field is attempted regardless: a full store is a property of the
   * store, not of one key, but stopping early would make the result depend on
   * field order.
   */
  save(prefs, level) {
    let stored = true;
    if (prefs.tableDensity) stored = this.setTableDensity(prefs.tableDensity, level) && stored;
    if (prefs.regionAliasPin != null) {
      stored = this.setRegionAliasPin(prefs.regionAliasPin, level) && stored;
    }
    if (prefs.graphViewMode) stored = this.setGraphViewMode(prefs.graphViewMode, level) && stored;
    if (typeof prefs.graphAssetColors === "boolean") stored = this.setGraphAssetColors(prefs.graphAssetColors, level) && stored;
    if (prefs.graphDetailTier) {
      stored = this.setGraphDetailTier(prefs.graphDetailTier, level) && stored;
    }
    if (prefs.activityView) stored = this.setActivityView(prefs.activityView, level) && stored;
    if (prefs.liveMode) stored = this.setLiveMode(prefs.liveMode, level) && stored;
    if (prefs.inspector != null) stored = this.setInspector(prefs.inspector, level) && stored;
    if (prefs.attributionChips) {
      stored = this.setAttributionChips(prefs.attributionChips, level) && stored;
    }
    if (prefs.quickActions) stored = this.setQuickActions(prefs.quickActions, level) && stored;
    if (prefs.columnVisibility) {
      for (const [tableId, visibility] of Object.entries(prefs.columnVisibility)) {
        stored = this.setColumnVisibility(tableId, visibility, level) && stored;
      }
    }
    if (prefs.graphHiddenPanels) {
      for (const [graphId, hidden] of Object.entries(prefs.graphHiddenPanels)) {
        stored = this.setGraphHiddenPanels(graphId, hidden, level) && stored;
      }
    }
    if (prefs.graphGrouping) {
      for (const [graphId, grouping] of Object.entries(prefs.graphGrouping)) {
        stored = this.setGraphGrouping(graphId, grouping, level) && stored;
      }
    }
    return stored;
  }
};
function createDisplayPrefsStore(storage) {
  return new DisplayPrefsStore(storage);
}
var MINIMUM_AMOUNT_DESCRIPTION = "Requested minimum amount; this does not prove fulfillment. Check the execution for the actual delivered amount.";
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
function hasUtxoAddressFormat(format) {
  return Boolean(format.bech32Hrp || format.base58Versions.length || format.cashaddrPrefix);
}
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u", tex: "tex" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validCosmosAddress(address, hrp) {
  const decoded = decodeBech32(address);
  return decoded !== null && decoded.hrp === hrp && decoded.constant === BECH32_CONST && [20, 32].includes(convertedLength(decoded.data) ?? -1);
}
function convertBits(data) {
  let bits = 0, accumulator = 0;
  const out = [];
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push(accumulator >> bits & 255);
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? out : null;
}
function validSegwitAddress(address, hrp) {
  const decoded = decodeBech32(address);
  if (!decoded || decoded.hrp !== hrp || !decoded.data.length) return false;
  const version = decoded.data[0];
  if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
  const length = convertedLength(decoded.data.slice(1));
  return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
}
var CASHADDR_GENERATORS = [0x98f2bc8e61n, 0x79b76d99e2n, 0xf33e5fb3c4n, 0xae2eabe2a8n, 0x1e4f43e470n];
var CASHADDR_HASH_BYTES = [20, 24, 28, 32, 40, 48, 56, 64];
function validCashAddr(address, prefix) {
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return false;
  let body = lower;
  const colon = lower.indexOf(":");
  if (colon >= 0) {
    if (lower.slice(0, colon) !== prefix) return false;
    body = lower.slice(colon + 1);
  }
  const data = [...body].map((c) => BECH32.indexOf(c));
  if (data.length < 9 || data.some((v) => v < 0)) return false;
  let checksum = 1n;
  for (const value of [...[...prefix].map((c) => c.charCodeAt(0) & 31), 0, ...data]) {
    const top = checksum >> 35n;
    checksum = (checksum & 0x07ffffffffn) << 5n ^ BigInt(value);
    CASHADDR_GENERATORS.forEach((generator, bit) => {
      if (top >> BigInt(bit) & 1n) checksum ^= generator;
    });
  }
  if ((checksum ^ 1n) !== 0n) return false;
  const payload = convertBits(data.slice(0, -8));
  if (!payload?.length) return false;
  const version = payload[0];
  return (version & 128) === 0 && payload.length - 1 === CASHADDR_HASH_BYTES[version & 7];
}
function validUtxoAddress(address, format) {
  if (address.length < 14 || address.length > 90) return false;
  const folded = address.toLowerCase();
  if (format.bech32Hrp && folded.startsWith(`${format.bech32Hrp}1`)) return validSegwitAddress(address, format.bech32Hrp);
  if (format.cashaddrPrefix && (folded.startsWith(`${format.cashaddrPrefix}:`) || /^[qp]/i.test(address))) {
    return validCashAddr(address, format.cashaddrPrefix);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && format.base58Versions.includes(payload[0]);
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.toLowerCase().startsWith(`${format.tex}1`)) {
    const tex = decodeBech32(address, 90);
    return !!tex && tex.hrp === format.tex && tex.constant === BECH32M_CONST && convertedLength(tex.data) === 20;
  }
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/tx.ts
function normalizeTonHash(value) {
  const h = value.trim();
  if (/^[0-9a-fA-F]{64}$/.test(h)) return h.toLowerCase();
  if (!/^[A-Za-z0-9+/_-]{43}=?$/.test(h)) return null;
  try {
    const standard = h.replace(/-/g, "+").replace(/_/g, "/").replace(/=$/, "");
    const bytes = atob(`${standard}=`);
    if (bytes.length !== 32 || btoa(bytes).replace(/=$/, "") !== standard) return null;
    return Array.from(bytes, (c) => c.charCodeAt(0).toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}
var RE_HEX64 = /^(0x)?[0-9a-fA-F]{64}$/i;
var RE_SOLANA_SIG = /^[1-9A-HJ-NP-Za-km-z]{64,88}$/;
function normalizeStarknetHash(value) {
  if (!/^0x[0-9a-fA-F]{1,64}$/.test(value)) return null;
  const n = BigInt(value);
  if (n >= 2n ** 251n + 17n * 2n ** 192n + 1n) return null;
  return `0x${n.toString(16).padStart(64, "0")}`;
}
var BARE_HEX_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.TON,
  ChainFamily.UTXO,
  ChainFamily.XRPL,
  ChainFamily.TRON,
  ChainFamily.COSMOS
]);
function normalizeTxHash(txHash, chainId) {
  const h = txHash.trim();
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : null;
  if (fam === ChainFamily.TON) return normalizeTonHash(h) ?? h;
  if (fam === ChainFamily.STARKNET) return normalizeStarknetHash(h) ?? h;
  if (fam === ChainFamily.SOLANA || !RE_HEX64.test(h) && RE_SOLANA_SIG.test(h)) {
    return h;
  }
  if (RE_HEX64.test(h)) {
    const bare = h.replace(/^0x/i, "").toLowerCase();
    if (fam !== null && BARE_HEX_FAMILIES.has(fam)) return bare;
    return hasEvmIdentifiers(fam) || /^0x/i.test(h) ? `0x${bare}` : bare;
  }
  return h;
}
function isValidTxHash(txHash, chainId) {
  const h = txHash.trim();
  if (!h) return false;
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : null;
  if (fam === ChainFamily.APTOS) return /^0x[0-9a-fA-F]{64}$/.test(h);
  if (fam === ChainFamily.STARKNET) return normalizeStarknetHash(h) !== null;
  if (fam === ChainFamily.TON) return normalizeTonHash(h) !== null;
  if (fam === ChainFamily.NEAR || fam === ChainFamily.SUI) return h.length >= 32 && h.length <= 44 && decodeBase58(h)?.length === 32;
  if (fam === ChainFamily.SOLANA) return RE_SOLANA_SIG.test(h);
  if (fam === ChainFamily.XRPL || fam === ChainFamily.TRON || fam === ChainFamily.COSMOS) return /^[0-9a-fA-F]{64}$/.test(h);
  if (hasEvmIdentifiers(fam) || fam === ChainFamily.UTXO) return RE_HEX64.test(h);
  return RE_HEX64.test(h) || RE_SOLANA_SIG.test(h) || normalizeTonHash(h) !== null || h.length >= 32 && h.length <= 44 && decodeBase58(h)?.length === 32;
}
function crossChainGroupingKey(input) {
  const chain = getChainMetadata(input.chainId);
  const chainKey = chain?.slug ?? (input.chainId ?? "unknown").toString().toLowerCase();
  return `${chainKey}:${normalizeTxHash(input.txHash, input.chainId)}`;
}
function groupByTx(records, accessor) {
  const groups = /* @__PURE__ */ new Map();
  for (const record of records) {
    const key = crossChainGroupingKey(accessor(record));
    const bucket = groups.get(key);
    if (bucket) bucket.push(record);
    else groups.set(key, [record]);
  }
  return groups;
}

// src/chains/ton-address.ts
function parseTonAddress(value) {
  value = value.trim();
  const raw = /^(-?\d+):([0-9a-f]{64})$/i.exec(value.trim());
  if (raw) {
    const workchain2 = Number(raw[1]);
    return Number.isInteger(workchain2) && workchain2 >= -128 && workchain2 <= 127 ? { address: `${workchain2}:${raw[2].toLowerCase()}`, testOnly: false } : void 0;
  }
  if (!/^[A-Za-z0-9+/_-]{48}$/.test(value)) return void 0;
  let bytes;
  try {
    bytes = Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/")), (char) => char.charCodeAt(0));
  } catch {
    return void 0;
  }
  if (bytes.length !== 36 || ![17, 81, 145, 209].includes(bytes[0])) return void 0;
  let crc = 0;
  for (const byte of bytes.slice(0, 34)) {
    crc ^= byte << 8;
    for (let bit = 0; bit < 8; bit++) crc = (crc << 1 ^ (crc & 32768 ? 4129 : 0)) & 65535;
  }
  if (crc !== (bytes[34] << 8 | bytes[35])) return void 0;
  const workchain = bytes[1] > 127 ? bytes[1] - 256 : bytes[1];
  return { address: `${workchain}:${bytes.slice(2, 34).map((byte) => byte.toString(16).padStart(2, "0")).join("")}`, testOnly: Boolean(bytes[0] & 128) };
}
function normalizeTonAddress(value) {
  return parseTonAddress(value)?.address;
}
function isValidTonAddress(value, isTestnet) {
  const parsed = parseTonAddress(value);
  return Boolean(parsed && (isTestnet || !parsed.testOnly));
}

// src/chains/custodialParties.ts
var CUSTODIAL_ADDRESS_PREFIX = "custodial:";
function custodialOriginOf(address) {
  if (typeof address !== "string" || !address.startsWith(CUSTODIAL_ADDRESS_PREFIX)) return null;
  const [bridge, asset, ...rest] = address.slice(CUSTODIAL_ADDRESS_PREFIX.length).split(":");
  return bridge && asset && rest.length === 0 ? { bridge, asset } : null;
}
var titleCase2 = (value) => value.replace(/(^|[_\s-])(\w)/g, (_, sep, ch) => `${sep ? " " : ""}${ch.toUpperCase()}`);
function custodialOriginName(address) {
  const origin = custodialOriginOf(address);
  if (origin === null) return null;
  const bridge = titleCase2(origin.bridge);
  return {
    label: `${origin.asset.toUpperCase()} deposited with ${bridge}`,
    title: `${bridge} custodial bridge \xB7 the deposit side: no on-chain reference links a deposit to its payout \xB7 not an account`
  };
}
function custodialOrderAccountOf(node) {
  const mark = node?.custodial_order;
  if (!mark || typeof mark !== "object") return null;
  const { bridge, entity } = mark;
  return typeof bridge === "string" && bridge ? { bridge, entity: typeof entity === "string" && entity ? entity : titleCase2(bridge) } : null;
}

// src/chains/hypercoreParties.ts
var SPOT_ORDER_BOOK_PREFIX = "hyperliquid:spot:";
var HYPERLIQUID_BRIDGE_ADDRESS = "hyperliquid:bridge";
function spotOrderBookIndexOf(address) {
  if (typeof address !== "string" || !address.startsWith(SPOT_ORDER_BOOK_PREFIX)) return null;
  const digits = address.slice(SPOT_ORDER_BOOK_PREFIX.length);
  return /^[0-9]+$/.test(digits) ? Number(digits) : null;
}
function spotOrderBookLabel(index) {
  return `Hyperliquid spot order book @${index}`;
}
function hypercorePartyName(address) {
  const book = spotOrderBookIndexOf(address);
  if (book !== null) {
    return {
      label: spotOrderBookLabel(book),
      title: "HyperCore spot order book \xB7 stands in for the makers a fill does not name \xB7 not an account"
    };
  }
  if (address === HYPERLIQUID_BRIDGE_ADDRESS) {
    return { label: "Hyperliquid bridge", title: "Hyperliquid's USDC bridge on HyperCore \xB7 not an account" };
  }
  return null;
}

// src/chains/syntheticParties.ts
function syntheticPartyName(address) {
  return hypercorePartyName(address) ?? custodialOriginName(address);
}

// src/chains/validator.ts
var VALIDATOR_ADDRESS_PREFIX = "beacon:validator:";
function validatorIndexOf(address) {
  if (typeof address !== "string" || !address.startsWith(VALIDATOR_ADDRESS_PREFIX)) return null;
  const digits = address.slice(VALIDATOR_ADDRESS_PREFIX.length);
  return /^[0-9]+$/.test(digits) ? Number(digits) : null;
}
function validatorLabel(index) {
  return `Validator ${index}`;
}
var VALIDATOR_TITLE = "Beacon-chain validator \xB7 not an execution account";

// src/chains/address.ts
var EVM_ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var validStarknetAddress = (value) => /^0x[0-9a-fA-F]{1,64}$/.test(value) && BigInt(value) < 2n ** 251n - 256n;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|ltc1|tltc1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var utxoRows = globalSingleton("chains/utxoFormatRows", () => ({
  version: -1,
  rows: []
}));
function utxoFormatRows() {
  if (utxoRows.version !== registry.version) {
    utxoRows.rows = registry.all().filter((chain) => chain.family === ChainFamily.UTXO && hasUtxoAddressFormat(chain));
    utxoRows.version = registry.version;
  }
  return utxoRows.rows;
}
function validStatedUtxoAddress(address) {
  return utxoFormatRows().some((chain) => validUtxoAddress(address, chain));
}
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validStatedUtxoAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  if (chainId && resolveChain(chainId)?.caip2.startsWith("tvm:")) return normalizeTonAddress(addr) ?? addr;
  const fam = addressFamily(addr, chainId);
  if (addr.includes("::")) {
    return fam === ChainFamily.SUI ? addr.replace(/0x[0-9a-fA-F]{1,64}(?=::)/g, (value) => `0x${value.slice(2).toLowerCase().padStart(64, "0")}`) : addr;
  }
  if (fam === ChainFamily.STARKNET && validStarknetAddress(addr)) return `0x${BigInt(addr).toString(16).padStart(64, "0")}`;
  if (fam === ChainFamily.SUI && /^0x[0-9a-fA-F]{1,64}$/.test(addr)) return `0x${addr.slice(2).toLowerCase().padStart(64, "0")}`;
  if (fam === ChainFamily.APTOS && /^0x[0-9a-fA-F]{1,64}$/.test(addr)) {
    const value = BigInt(addr);
    return value < 16n ? `0x${value.toString(16)}` : `0x${value.toString(16).padStart(64, "0")}`;
  }
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === ChainFamily.COSMOS && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  if (chainId && resolveChain(chainId)?.caip2.startsWith("tvm:")) return isValidTonAddress(addr, Boolean(resolveChain(chainId)?.isTestnet));
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
    case ChainFamily.HYPERCORE:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO: {
      const chain = chainId ? resolveChain(chainId) : null;
      if (!chain) return validStatedUtxoAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr);
      if (hasUtxoAddressFormat(chain)) return validUtxoAddress(addr, chain);
      if (ZCASH_ADDRESS_NETWORKS[chain.slug]) return validZcashAddress(addr, chain.slug);
      if (MONERO_ADDRESS_NETWORKS[chain.slug]) return validMoneroAddress(addr, chain.slug);
      return true;
    }
    case ChainFamily.XRPL:
      return RE_XRPL.test(addr) && validLedgerAddress(addr, "xrpl");
    case ChainFamily.TRON:
      return RE_TRON.test(addr) && validLedgerAddress(addr, "tron");
    case ChainFamily.STARKNET:
      return validStarknetAddress(addr);
    case ChainFamily.SUI:
    case ChainFamily.APTOS:
      return /^0x[0-9a-fA-F]{1,64}$/.test(addr);
    case ChainFamily.NEAR:
      return addr.length >= 2 && addr.length <= 64 && /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(addr);
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32;
    case ChainFamily.COSMOS: {
      const hrp = chainId ? resolveChain(chainId)?.bech32Hrp : null;
      return hrp ? validCosmosAddress(addr, hrp) || validCosmosAddress(addr, `${hrp}valoper`) : RE_COSMOS_BECH32.test(addr);
    }
    default:
      return RE_EVM.test(addr) || validStatedUtxoAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr) || RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32 || RE_COSMOS_BECH32.test(addr);
  }
}
function defaultChainsForAddress(address) {
  const family = addressFamily(address);
  if (family === null || hasEvmIdentifiers(family)) return [];
  return registry.all().filter((chain) => chain.family === family && !chain.isTestnet && isValidAddress(address, chain.slug)).map((chain) => chain.slug).sort();
}
function isEvmAddress(address) {
  return RE_EVM.test(address.trim());
}
function isZeroAddress(address) {
  return Boolean(address) && normalizeAddress(String(address)) === EVM_ZERO_ADDRESS;
}
function addressesEqual(a, b, chainId) {
  if (!a || !b) return false;
  return normalizeAddress(a, chainId) === normalizeAddress(b, chainId);
}
function shortenAddress(address, lengths) {
  const validator = validatorIndexOf(address);
  if (validator !== null) return validatorLabel(validator);
  const party = syntheticPartyName(address);
  if (party !== null) return party.label;
  return truncateMiddle(address, { ...getTruncation("address"), ...lengths });
}
function shortenHash(hash, lengths) {
  return truncateMiddle(hash, { ...getTruncation("hash"), ...lengths });
}
function shortenIdentifier(value) {
  return truncateMiddle(value, getTruncation(truncationKindOf(value)));
}
function shortenCompositeIdentifier(value) {
  return value.split(/([:#/])/).map((part) => part === ":" || part === "#" || part === "/" ? part : shortenIdentifier(part)).join("");
}
var LONGER_THAN_AN_ADDRESS = /^0x[0-9a-f]{41,}$/i;
function truncationKindOf(value) {
  const text2 = value.trim();
  return isValidTxHash(text2) || LONGER_THAN_AN_ADDRESS.test(text2) ? "hash" : "address";
}

// src/value/netflow.ts
function tokenKeyOf(t) {
  return `${t.chainId ?? "*"}:${t.token.toLowerCase()}`;
}
function computeNetFlow(transfers, focalAddress) {
  const map = /* @__PURE__ */ new Map();
  for (const t of transfers) {
    const amount = typeof t.amount === "bigint" ? t.amount : BigInt(t.amount);
    const key = tokenKeyOf(t);
    let entry = map.get(key);
    if (!entry) {
      entry = {
        tokenKey: key,
        token: t.token,
        symbol: t.symbol ?? null,
        chainId: t.chainId ?? null,
        decimals: t.decimals,
        in: 0n,
        out: 0n,
        net: 0n,
        priceUsd: t.priceUsd ?? null
      };
      map.set(key, entry);
    }
    if (entry.priceUsd == null && t.priceUsd != null) entry.priceUsd = t.priceUsd;
    if (addressesEqual(focalAddress, t.to, t.chainId)) entry.in += amount;
    if (addressesEqual(focalAddress, t.from, t.chainId)) entry.out += amount;
  }
  for (const entry of map.values()) {
    entry.net = entry.in - entry.out;
    if (entry.priceUsd != null) entry.netUsd = tokenToUsd(entry.net, entry.decimals, entry.priceUsd);
  }
  return map;
}
function summarizeSwap(transfers, focalAddress) {
  const entries = [...computeNetFlow(transfers, focalAddress).values()].filter((e) => e.net !== 0n);
  return {
    sent: entries.filter((e) => e.net < 0n),
    received: entries.filter((e) => e.net > 0n)
  };
}
function aggregateNetUsd(entries) {
  let total = 0;
  for (const e of entries) if (e.netUsd != null) total += e.netUsd;
  return total;
}
function counterparties(transfers, focalAddress) {
  const set = /* @__PURE__ */ new Set();
  for (const t of transfers) {
    const focalIsFrom = addressesEqual(focalAddress, t.from, t.chainId);
    const focalIsTo = addressesEqual(focalAddress, t.to, t.chainId);
    if (focalIsFrom && !focalIsTo) set.add(normalizeAddress(t.to, t.chainId));
    if (focalIsTo && !focalIsFrom) set.add(normalizeAddress(t.from, t.chainId));
  }
  return [...set];
}

// src/value/token-amount-fact.ts
var UNLIMITED_NOTE = (raw) => `No limit \u2014 the maximum an approval can carry (${raw} base units). A spender holding this allowance can move the whole balance, now and later.`;
var PRESCALED_NOTE = "Published already scaled \u2014 this fact carries a fraction, and base units are whole numbers. The figure is the emitter's own; no scale of ours was applied.";
var BASE_UNITS_NOTE = "Base units, exactly as the protocol published them. This fact carries no decimals for the asset, so it is not scaled \u2014 scaling it by a guess would turn it into a quantity that reads as real.";
var PRESCALED = /^(-?\d+\.\d+)(?:\s+([A-Za-z][A-Za-z0-9._-]{0,15}))?$/;
function decimalsOf(attr) {
  const value = attr?.decimals;
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= 36 ? n : null;
}
function symbolOf(attr) {
  const value = attr?.symbol;
  if (typeof value !== "string" || !value.trim()) return void 0;
  return safeTicker(value).text || void 0;
}
function isStructuredTokenAmount(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const amount = value;
  return typeof amount.amount_raw === "string" && /^-?\d+$/.test(amount.amount_raw) || typeof amount.amount === "string" && /^-?\d+(?:\.\d+)?$/.test(amount.amount);
}
function tokenAmountFace(value, attr) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const amount = value;
    const raw = amount.amount_raw;
    const symbol2 = symbolOf(amount) ?? symbolOf(attr);
    const decimals2 = decimalsOf(amount);
    if (typeof raw === "string" && /^-?\d+$/.test(raw) && decimals2 !== null) {
      return tokenAmountFace(raw, { decimals: decimals2, symbol: symbol2 });
    }
    if (typeof amount.amount === "string" && /^-?\d+(?:\.\d+)?$/.test(amount.amount)) {
      return {
        text: formatBalanceCell(amount.amount)?.text ?? amount.amount,
        title: `${amount.amount}${symbol2 ? ` ${symbol2}` : ""} \u2014 Published already scaled`,
        symbol: symbol2,
        scaled: true
      };
    }
    if (typeof raw === "string") return tokenAmountFace(raw, { symbol: symbol2 });
  }
  const text2 = typeof value === "string" ? value : String(value ?? "");
  const decimals = decimalsOf(attr);
  const symbol = symbolOf(attr);
  if (isUnlimitedAmount(text2)) {
    return {
      text: "Unlimited",
      title: UNLIMITED_NOTE(text2),
      ...symbol ? { symbol } : {},
      // Not a scaled number — there is no quantity here to scale.
      scaled: false,
      unlimited: true
    };
  }
  if (decimals === null) {
    const prescaled = PRESCALED.exec(text2.trim());
    if (prescaled) {
      const [, digits, unit] = prescaled;
      const ticker = symbol ?? (unit ? safeTicker(unit).text || void 0 : void 0);
      const cell = formatBalanceCell(digits);
      return {
        text: cell?.text ?? digits,
        title: `${text2} \u2014 ${PRESCALED_NOTE}`,
        ...ticker ? { symbol: ticker } : {},
        scaled: true
      };
    }
    return { text: text2, title: BASE_UNITS_NOTE, ...symbol ? { symbol } : {}, scaled: false };
  }
  const formatted = formatTokenAmount(
    text2,
    { symbol, decimals },
    { maxFractionDigits: 6, withSymbol: false }
  );
  return {
    text: formatted,
    title: `${formatTokenDisplay(text2, decimals, { maxFractionDigits: decimals })}${symbol ? ` ${symbol}` : ""} \u2014 ${text2} base units`,
    ...symbol ? { symbol } : {},
    scaled: true
  };
}

// src/value/index.ts
function parseUnits(value, decimals) {
  const negative = value.trim().startsWith("-");
  const clean = value.trim().replace(/^[-+]/, "");
  const [whole, frac = ""] = clean.split(".");
  const fracPadded = (frac + "0".repeat(decimals)).slice(0, decimals);
  const combined = `${whole || "0"}${fracPadded}`.replace(/^0+(?=\d)/, "");
  const result = BigInt(combined || "0");
  return negative ? -result : result;
}
function toBaseUnits(value) {
  if (typeof value === "bigint") return value;
  const text2 = value.trim();
  if (!/^[-+]?\d+$/.test(text2)) return void 0;
  try {
    return BigInt(text2);
  } catch {
    return void 0;
  }
}
function formatUnits(value, decimals) {
  const parsed = toBaseUnits(value);
  const scale = Number.isInteger(decimals) && decimals >= 0 ? decimals : void 0;
  if (parsed === void 0 || scale === void 0) {
    return typeof value === "string" ? value.trim() : String(value);
  }
  let v = parsed;
  const negative = v < 0n;
  if (negative) v = -v;
  const base = 10n ** BigInt(scale);
  const whole = v / base;
  const frac = v % base;
  if (scale === 0) return `${negative ? "-" : ""}${whole.toString()}`;
  const fracStr = frac.toString().padStart(scale, "0").replace(/0+$/, "");
  const body = fracStr ? `${whole.toString()}.${fracStr}` : whole.toString();
  return `${negative ? "-" : ""}${body}`;
}
function tokenToUsd(amount, decimals, priceUsd) {
  const human = Number(formatUnits(amount, decimals));
  if (!Number.isFinite(human) || !Number.isFinite(priceUsd)) return 0;
  return human * priceUsd;
}
function aggregateUsd(holdings) {
  let total = 0;
  for (const h of holdings) {
    if (h.priceUsd == null) continue;
    total += tokenToUsd(h.amount, h.decimals, h.priceUsd);
  }
  return total;
}
function sumAmounts(amounts) {
  let total = 0n;
  for (const a of amounts) total += toBaseUnits(a) ?? 0n;
  return total;
}
var USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
var SUB_CENT_USD = 5e-3;
function subCentUsdBound(value) {
  return value < 0 ? ">-$0.01" : "<$0.01";
}
function formatShare(value) {
  if (!Number.isFinite(value) || value === 0) return "0%";
  const pct = value * 100;
  if (Math.abs(pct) < 1) return pct > 0 ? "<1%" : ">-1%";
  if (pct > 99.95 && pct < 100) return ">99.9%";
  const rounded = Math.round(pct * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
}
function formatPercentCell(percent, opts = {}) {
  if (!Number.isFinite(percent) || percent === 0) return "0%";
  const digits = opts.maxFractionDigits ?? 4;
  const floor = 10 ** -digits;
  if (Math.abs(percent) < floor / 2) return `${percent > 0 ? "<" : ">-"}${floor.toFixed(digits)}%`;
  return `${percent.toLocaleString(void 0, { maximumFractionDigits: digits })}%`;
}
function formatUsd(value, opts = {}) {
  if (value !== 0 && Number.isFinite(value) && Math.abs(value) < SUB_CENT_USD) {
    return subCentUsdBound(value);
  }
  if (opts.compact || Math.abs(value) >= 1e4) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 2
    }).format(value);
  }
  return USD_FORMATTER.format(value);
}
var COMPACT_BALANCE = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2
});
function formatTokenBalance(value) {
  if (value == null || !Number.isFinite(value)) return "\u2014";
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 1e6) return COMPACT_BALANCE.format(value);
  if (abs >= 1) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  if (abs < 1e-4) return value < 0 ? ">-0.0001" : "<0.0001";
  return value.toLocaleString("en-US", { maximumFractionDigits: 6 });
}
function formatBalanceCell(exact) {
  const value = String(exact ?? "").trim();
  if (!value) return null;
  const numeric = Number(value);
  if (DECIMAL_RE.test(value) && Number.isFinite(numeric)) {
    const text2 = formatTokenBalance(numeric);
    return { text: isZeroText(text2) && /[1-9]/.test(value) ? "<0.0001" : text2, title: value };
  }
  const display = formatGraphAmount(value, { maxChars: 16 });
  return display ? { text: display.text, title: value } : null;
}
var SUB_PRECISION_SIGNIFICANT_DIGITS = 2;
var SUB_PRECISION_MAX_FRACTION = 12;
var MAX_PLAIN_DECIMAL_CHARS = 40;
function subPrecisionAmount(whole, fraction, maxFrac) {
  const firstSignificant = fraction.search(/[1-9]/);
  const keep = firstSignificant + SUB_PRECISION_SIGNIFICANT_DIGITS;
  if (firstSignificant >= 0 && keep <= SUB_PRECISION_MAX_FRACTION) {
    const kept = fraction.slice(0, keep).replace(/0+$/, "");
    return `${whole}.${kept}`;
  }
  if (!isZeroText(whole)) return whole;
  const bound = `0.${"0".repeat(Math.max(maxFrac - 1, 0))}1`;
  return whole.startsWith("-") ? `>-${bound}` : `<${bound}`;
}
var UNLIMITED_AMOUNTS = [
  (1n << 256n) - 1n,
  (1n << 255n) - 1n,
  (1n << 96n) - 1n
];
function isUnlimitedAllowance(value, bits) {
  if (bits !== 160) return typeof value === "string" || typeof value === "bigint" ? isUnlimitedAmount(value) : false;
  if (typeof value !== "string" && typeof value !== "bigint") return false;
  try {
    return BigInt(value) === (1n << 160n) - 1n;
  } catch {
    return false;
  }
}
var UNLIMITED_DIGITS = new Set(UNLIMITED_AMOUNTS.map((value) => value.toString()));
function isUnlimitedAmount(value) {
  if (value == null || typeof value === "number") return false;
  const digits = String(value).trim().replace(/[,\s_]/g, "").replace(/^[-+]/, "").replace(".", "").replace(/^0+(?=\d)/, "");
  return UNLIMITED_DIGITS.has(digits);
}
function formatTokenDisplay(amount, decimals, opts = {}) {
  const full = formatUnits(amount, decimals);
  const maxFrac = opts.maxFractionDigits ?? 6;
  let out = full;
  if (full.includes(".")) {
    const w = full.split(".")[0] ?? "0";
    const f = full.split(".")[1] ?? "";
    const kept = f.slice(0, maxFrac).replace(/0+$/, "");
    out = kept ? `${w}.${kept}` : subPrecisionAmount(w, f, maxFrac);
  }
  return opts.symbol ? `${out} ${opts.symbol}` : out;
}
function isZeroText(value) {
  return value === "0" || value === "-0";
}
function toPlainDecimalString(value) {
  if (!Number.isFinite(value)) return String(value);
  const text2 = String(value);
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(text2);
  if (!match) return text2;
  const [, sign, whole, fraction = "", exponentText] = match;
  const digits = `${whole}${fraction}`;
  const exponent = Number(exponentText);
  const point = whole.length + exponent;
  const expanded = point <= 0 ? digits.length - point + 2 : Math.max(point, digits.length) + 1;
  if (expanded > MAX_PLAIN_DECIMAL_CHARS) return text2;
  if (point <= 0) return `${sign}0.${"0".repeat(-point)}${digits}`;
  if (point >= digits.length) return `${sign}${digits}${"0".repeat(point - digits.length)}`;
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
}
function formatGraphAmount(amount, opts = {}) {
  if (amount == null) return null;
  const exact = String(amount).trim();
  if (!exact) return null;
  if (isUnlimitedAmount(exact)) return { text: "Unlimited", title: exact, abbreviated: true };
  const maxChars = opts.maxChars ?? 12;
  if (exact.length <= maxChars) {
    return { text: DECIMAL_RE.test(exact) ? groupWhole(exact) : exact, title: exact, abbreviated: false };
  }
  const numeric = DECIMAL_RE.test(exact) ? Number(exact) : Number.NaN;
  if (Number.isFinite(numeric)) {
    if (Math.abs(numeric) >= 1e6) {
      const compact = formatTokenBalance(numeric);
      if (compact.length > maxChars) {
        return { text: numeric.toExponential(2), title: exact, abbreviated: true };
      }
      return { text: compact, title: exact, abbreviated: true };
    }
    const [whole = "", fraction = ""] = exact.split(".");
    const room = Math.max(2, maxChars - whole.length - 2);
    if (fraction.length > room) {
      const kept = fraction.slice(0, room).replace(/0+$/, "");
      if (!kept) {
        return { text: subPrecisionAmount(whole, fraction, room), title: exact, abbreviated: true };
      }
      return { text: `${groupWhole(whole)}.${kept}\u2026`, title: exact, abbreviated: true };
    }
  }
  return { text: `${exact.slice(0, maxChars)}\u2026`, title: exact, abbreviated: true };
}
var DECIMAL_RE = /^-?\d+(?:\.\d+)?$/;
function groupWhole(decimal) {
  const [whole = "", fraction] = decimal.split(".");
  const sign = whole.startsWith("-") ? "-" : "";
  const digits = whole.slice(sign.length).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === void 0 ? `${sign}${digits}` : `${sign}${digits}.${fraction}`;
}
var LONG_NUMBER_RE = /(?<![0-9A-Za-z])\d[\d,]*(?:\.\d+)?(?![0-9A-Za-z])/g;
function shortenLongNumbers(text2, opts = {}) {
  const maxDigits = opts.maxDigits ?? 12;
  return text2.replace(LONG_NUMBER_RE, (match) => {
    const bare = match.replace(/,/g, "");
    if (isUnlimitedAmount(bare)) return "Unlimited";
    if (match.length <= maxDigits) return match;
    const display = formatGraphAmount(bare, { maxChars: maxDigits });
    return display ? display.text : match;
  });
}
var DECIMAL_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
function coerceNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value !== "string") return void 0;
  const text2 = value.trim();
  if (text2 === "" || !DECIMAL_TEXT.test(text2)) return void 0;
  const parsed = Number(text2);
  return Number.isFinite(parsed) ? parsed : void 0;
}

export { ACTIVITY_VIEWS, ACTIVITY_VIEW_LABELS, ATTRIBUTION_KINDS, AUTHORABLE_NODE_KINDS, AUTHORED_AT_ATTR, AUTHORED_BY_ATTR, AUTHORED_REASON_ATTR, AUTHORED_SCOPE_ATTR, BASE_UNITS_LABEL, CHAIN_TEXT_MAX_CHARS, CHAIN_TICKER_MAX_CHARS, CONTESTED_ATTR, CUSTODIAL_ADDRESS_PREFIX, DEFAULT_ATTRIBUTION_CHIP_PREFS, DEFAULT_GRAPH_DETAIL_TIER, DEFAULT_LIVE_REFRESH_MS, DEFAULT_REGION_ALIAS_PIN, DEFAULT_TABLE_DENSITY, DEFAULT_TRUNCATION, DISPLAY_PREF_KEYS, DisplayPrefsStore, ELLIPSIS, EVM_ZERO_ADDRESS, EvidenceSchema, EvidenceSourceSchema, FIELD_LABELS, GRAPH_DETAIL_TIERS, GRAPH_DETAIL_TIER_LABELS, GRAPH_FLOW_VIEW_MODES, GRAPH_KINDS, GRAPH_LAYOUT_HINTS, GRAPH_NODE_KINDS, GRAPH_PANEL_KEYS, GRAPH_PANEL_LABELS, GRAPH_VIEW_MODES, GRAPH_VIEW_MODE_LABELS, GROUPING_DIMENSIONS, GraphSceneSchema, GroupingDimensionOptionsSchema, GroupingSpecSchema, HYPERLIQUID_BRIDGE_ADDRESS, IDENTITY_NAME_FALLBACK, LIVE_MODES, LIVE_MODE_LABELS, LocalStorageRequestCache, MEDIA_REF_KINDS, MERGED_COUNT_ATTR, MERGED_TXS_ATTR, MERGED_TXS_LIMIT, MINIMUM_AMOUNT_DESCRIPTION, MediaRefSchema, MemoryRequestCache, NATIVE_UNITS_LABEL, NODE_KIND_META, NO_CACHE, OFFCHAIN_SCENE_NODE_KINDS, OVERLAY_BODY_KEYS, PriceEngine, QUICK_ACTION_VISIBILITIES, SCENE_DETAIL_TIERS, SCENE_EDGE_KINDS, SCENE_ELEMENT_ORIGINS, SCENE_EMPTY_REASONS, SCENE_EMPTY_REASON_ATTR, SCENE_GROUP_TYPES, SCENE_NODE_KINDS, SPOT_ORDER_BOOK_PREFIX, SUBJECT_ANY, SUB_CENT_USD, SceneAssetSchema, SceneEdgeSchema, SceneFactSchema, SceneGroupSchema, SceneNodeSchema, SceneOverlaySchema, SceneSourceRefSchema, StaticPriceSource, TABLE_DENSITIES, TABLE_DENSITY_LABELS, TABLE_DENSITY_ROW_HEIGHT, TX_COUNT_ATTR, TX_FIELDS, TX_MEMBERSHIP_ATTR, UNLIMITED_AMOUNTS, UNSCALED_AMOUNT_NOTE, VALIDATOR_ADDRESS_PREFIX, VALIDATOR_TITLE, addressFamily, addressesEqual, aggregateNetUsd, aggregateUsd, allowedLiveMode, annotationLayers, asString, asTrimmedString, badgeForKind, cacheKey, chainText, coerceNumber, computeNetFlow, configureTruncation, countLabel, counterparties, createDisplayPrefsStore, crossChainGroupingKey, custodialOrderAccountOf, custodialOriginName, custodialOriginOf, decodeBase58, defaultChainsForAddress, defaultGroupingSpec, displayChainText, domainKindForSceneKind, emptySceneOverlay, entityDisplayName, fieldLabel, fieldLabels, formatBalanceCell, formatGraphAmount, formatPercentCell, formatShare, formatTokenAmount, formatTokenBalance, formatTokenDisplay, formatUnits, formatUsd, getTruncation, groupByTx, groupSceneLegs, hexPrefixOf, hypercorePartyName, idTail, indefiniteArticle, initials, isEvmAddress, isOffChainKind, isSceneOverlayBody, isStructuredTokenAmount, isTxCountSublabel, isUnlimitedAllowance, isUnlimitedAmount, isUnscaledAmount, isValidAddress, isValidTxHash, isZeroAddress, isZeroText, labelForKind, layerForSubject, liveModesFor, mergeAnnotationLayer, mergedCount, mergedTxRef, mergedTxRefs, nativeTokenMeta, nativeUnitLabel, nodeKindLabel, normalizeAddress, normalizeStarknetHash, normalizeTonHash, normalizeTxHash, num, parseGraphScene, parseGroupingSpec, parseSceneOverlay, parseUnits, pluralize, pluralizeLabel, priceRefKey, resetTruncationConfig, resolveAttributionChipPrefs, resolveDensity, resolveGraphViewMode, safeChainText, safeTicker, sentenceList, shortId, shortenAddress, shortenCompositeIdentifier, shortenHash, shortenIdentifier, shortenIdentifiersIn, shortenLongNumbers, spotOrderBookIndexOf, spotOrderBookLabel, str, subCentUsdBound, sumAmounts, summarizeSwap, syntheticPartyName, text, toPlainDecimalString, tokenAmountFace, tokenToUsd, truncateMiddle, truncationKindOf, txCountSublabel, txMembership, validatorIndexOf, validatorLabel, withArticle, writeInvalidationFragment };
//# sourceMappingURL=chunk-3PIYBOLI.js.map
//# sourceMappingURL=chunk-3PIYBOLI.js.map